import axios from 'axios';
import { isAuthenticated, getAccessToken } from '../features/auth/services/auth';
import { OrganizationPayload, OrganizationService } from '../types/types';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getAuthHeaders = async () => {
  const isAuth = await isAuthenticated();
  if (!isAuth) throw new Error("User is not authenticated");
  const token = getAccessToken();
  return {
    Authorization: `Bearer ${token}`,
    "ngrok-skip-browser-warning": "69420",
  };
};

function buildQueryParams(params: Record<string, any>): string {
  const query = new URLSearchParams();

  for (const key in params) {
    const value = params[key];

    if (
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0)
    ) {
      continue;
    }

    if (Array.isArray(value)) {
      query.append(key, value.join(","));
    } else {
      query.append(key, value.toString());
    }
  }

  return query.toString();
}

export const organizationsService: OrganizationService = {
  getPresignedUrl: async (fileName: string, fileType: string) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/api/organizations/presigned-url`,
      {
        fileName,
        fileType,
      },
      { headers }
    );
    return response.data;
  },

  uploadImage: async (url: string, file: File) => {
    await axios.put(url, file, {
      headers: { "Content-Type": file.type },
    });
  },

  createOrganization: async (organizationData: any) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/api/organizations`,
      organizationData,
      { headers }
    );
    return response.data;
  },

  updateOrganization: async (id: number, organizationData: any) => {
    const headers = await getAuthHeaders();
    const response = await axios.put(
      `${API_BASE_URL}/api/organizations/${id}`,
      organizationData,
      { headers }
    );
    return response.data;
  },

  getIndustries: async () => {
    const headers = await getAuthHeaders();
    // const response = await axios.get(`${API_BASE_URL}/api/industries`, { headers });
    return [
      { name: "Accounting", value: "Accounting" },
      { name: "Advertising", value: "Advertising" },
      { name: "Agriculture", value: "Agriculture" },
      { name: "Architecture", value: "Architecture" },
      { name: "Automotive", value: "Automotive" },
      { name: "Banking", value: "Banking" },
      { name: "Biotechnology", value: "Biotechnology" },
      { name: "Broadcasting", value: "Broadcasting" },
      { name: "Business Services", value: "Business Services" },
      { name: "Chemicals", value: "Chemicals" },
      { name: "Construction", value: "Construction" },
      { name: "Consulting", value: "Consulting" },
      { name: "Consumer Products", value: "Consumer Products" },
      { name: "Defense", value: "Defense" },
      { name: "Design", value: "Design" },
      { name: "E-Commerce", value: "E-Commerce" },
      { name: "Education", value: "Education" },
      { name: "Electronics", value: "Electronics" },
      { name: "Energy", value: "Energy" },
      { name: "Engineering", value: "Engineering" },
      { name: "Entertainment", value: "Entertainment" },
      { name: "Environmental", value: "Environmental" },
      { name: "Fashion", value: "Fashion" },
      { name: "Finance", value: "Finance" },
      { name: "Food & Beverage", value: "Food & Beverage" },
      { name: "Gaming", value: "Gaming" },
      { name: "Government", value: "Government" },
      { name: "Healthcare", value: "Healthcare" },
      { name: "Hospitality", value: "Hospitality" },
      { name: "Human Resources", value: "Human Resources" },
      { name: "Import/Export", value: "Import/Export" },
      { name: "Insurance", value: "Insurance" },
      { name: "Internet", value: "Internet" },
      { name: "IT Services", value: "IT Services" },
      { name: "Legal", value: "Legal" },
      { name: "Logistics", value: "Logistics" },
      { name: "Manufacturing", value: "Manufacturing" },
      { name: "Marketing", value: "Marketing" },
      { name: "Media", value: "Media" },
      { name: "Mining", value: "Mining" },
      { name: "Nonprofit", value: "Nonprofit" },
      { name: "Oil & Gas", value: "Oil & Gas" },
      { name: "Pharmaceuticals", value: "Pharmaceuticals" },
      { name: "Publishing", value: "Publishing" },
      { name: "Real Estate", value: "Real Estate" },
      { name: "Retail", value: "Retail" },
      { name: "Security", value: "Security" },
      { name: "Sports", value: "Sports" },
      { name: "Telecommunications", value: "Telecommunications" },
      { name: "Transportation", value: "Transportation" },
      { name: "Travel", value: "Travel" },
      { name: "Utilities", value: "Utilities" },
      { name: "Warehousing", value: "Warehousing" },
    ];
  },

  getOrganizationById: async (id: number) => {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_BASE_URL}/api/organizations/${id}`,
      { headers }
    );
    return response.data;
  },

  // getOrganizations: async (
  //   page: number,
  //   perPage: number,
  //   sort?: { field: string; direction: "ASC" | "DESC" },
  //   query?: string,
  //   filters: Record<string, any> = {},
  //   visibleColumns: string[] = []
  // ) => {
  //   const headers = await getAuthHeaders();

  //   const params: Record<string, any> = {
  //     page,
  //     perPage,
  //     query,
  //     ...filters,
  //     listingColumns: visibleColumns.length > 0 ? visibleColumns : undefined,
  //   };

  //   if (sort) {
  //     params.sortField = sort.field;
  //     params.sortDirection = sort.direction;
  //   }

  //   const queryString = buildQueryParams(params);
  //   const response = await axios.get(
  //     `${API_BASE_URL}/api/organizations?${queryString}`,
  //     {
  //       headers,
  //     }
  //   );

  //   return response.data;
  // },

  getOrganizationsMinimal: async () => {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_BASE_URL}/api/organizations/minimal`,
      { headers }
    );
    return response.data;
  },

  deleteOrganization: async (id: number) => {
    const headers = await getAuthHeaders();
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/organizations/${id}`,
        { headers }
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to delete organization");
    }
  },
  getEntityById: async (id: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/api/organizations/${id}`,
      { headers: await getAuthHeaders() }
    );
    return response.data;
  },

  createEntity: async (organizationData: OrganizationPayload) => {
    const response = await axios.post(
      `${API_BASE_URL}/api/organizations`,
      organizationData,
      { headers: await getAuthHeaders() }
    );
    return response.data;
  },

  updateEntity: async (id: number, organizationData: OrganizationPayload) => {
    const response = await axios.put(
      `${API_BASE_URL}/api/organizations/${id}`,
      organizationData,
      { headers: await getAuthHeaders() }
    );
    return response.data;
  },
  getDynamicColumns: async (scope: string, query: string = "", page: number = 1, perPage: number = 50) => {
    const headers = await getAuthHeaders();
    const params: Record<string, any> = {
            page,
            perPage,
            query
        };
    const response = await axios.get(
      `${API_BASE_URL}/api/search-query/columns?scope=${scope}`,
      { headers, params }
    );
    return response.data;
  },
  getDynamicFilters: async (scope: string) => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE_URL}/api/search-query/filters?scope=${scope}`, { headers });
    return response.data;
  }
};
